import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import type { PermissionPageType } from '../services/users.service';

interface PermissionRouteProps {
  children: React.ReactElement;
  pageType: PermissionPageType;
  requiredCompany?: string; // 특정 회사 권한이 필요한 경우
}

const PermissionRoute: React.FC<PermissionRouteProps> = ({ children, pageType, requiredCompany }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [permittedCompanies, setPermittedCompanies] = useState<string[]>([]);

  useEffect(() => {
    const checkPermission = async () => {
      if (!isAuthenticated || !user) {
        setHasPermission(false);
        return;
      }

      // Admin, Editor, Reviewer는 모든 권한 허용
      if (user.role === 'admin' || user.role === 'editor' || user.role === 'reviewer') {
        setHasPermission(true);
        return;
      }

      // Viewer는 사용자 권한 확인
      try {
        const response = await api.get(`/users/${user.id}/permissions`);

        const permissions = response.data;
        const hasPagePermission = permissions.some(
          (p: any) => p.pageType === pageType
        );

        if (!hasPagePermission) {
          setHasPermission(false);
          return;
        }

        // 특정 회사 권한이 필요한 경우
        if (requiredCompany) {
          const hasCompanyPermission = permissions.some(
            (p: any) => p.pageType === pageType && p.companyName === requiredCompany
          );
          setHasPermission(hasCompanyPermission);
        } else {
          // 해당 pageType의 허용된 회사 목록 저장
          const companies = permissions
            .filter((p: any) => p.pageType === pageType)
            .map((p: any) => p.companyName);
          setPermittedCompanies(companies);
          setHasPermission(companies.length > 0);
        }
      } catch (error) {
        console.error('권한 확인 실패:', error);
        setHasPermission(false);
      }
    };

    checkPermission();
  }, [isAuthenticated, user, pageType, requiredCompany]);

  if (isLoading || hasPermission === null) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>접근 권한이 없습니다</h2>
        <p>이 페이지에 접근할 권한이 없습니다. 관리자에게 문의하세요.</p>
        <button onClick={() => window.history.back()}>돌아가기</button>
      </div>
    );
  }

  // children에 permittedCompanies 전달 (필요한 경우)
  return React.cloneElement(children, { permittedCompanies });
};

export default PermissionRoute;
